import React, { Component, ErrorInfo, ReactNode } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Paper,
  alpha,
  Chip
} from '@mui/material'
import {
  ErrorOutline,
  Refresh,
  Home,
  BugReport,
  Support
} from '@mui/icons-material'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  errorId: string | null
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    
    // Update state with error info
    this.setState({
      error,
      errorInfo
    })

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }

    // Log to external service (e.g., Sentry, LogRocket, etc.)
    this.logErrorToService(error, errorInfo)
  }

  private logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    // Here you would typically send the error to an external service
    // For now, we'll just log it to console with additional context
    const errorReport = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      errorId: this.state.errorId
    }

    console.error('Error Report:', errorReport)
    
    // Example: Send to external service
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, {
    //     contexts: {
    //       react: {
    //         componentStack: errorInfo.componentStack
    //       }
    //     },
    //     tags: {
    //       errorBoundary: true,
    //       errorId: this.state.errorId
    //     }
    //   })
    // }
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    })
  }

  private handleReload = () => {
    window.location.reload()
  }

  private handleGoHome = () => {
    window.location.href = '/'
  }

  private handleReportBug = () => {
    const errorReport = {
      error: this.state.error?.message,
      stack: this.state.error?.stack,
      componentStack: this.state.errorInfo?.componentStack,
      errorId: this.state.errorId,
      url: window.location.href,
      timestamp: new Date().toISOString()
    }

    // Create mailto link with error details
    const subject = encodeURIComponent(`WG-Anzeigen Error Report - ${this.state.errorId}`)
    const body = encodeURIComponent(`
WG-Anzeigen Error Report:

Error ID: ${this.state.errorId}
URL: ${window.location.href}
Timestamp: ${new Date().toISOString()}
Error Message: ${this.state.error?.message}

Please describe what you were doing when the error occurred:

[Your description here]

---
Technical Details:
${JSON.stringify(errorReport, null, 2)}
    `)

    window.open(`mailto:support@wg-anzeigen.de?subject=${subject}&body=${body}`)
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error UI
      return <ErrorFallback 
        error={this.state.error}
        errorId={this.state.errorId}
        onRetry={this.handleRetry}
        onReload={this.handleReload}
        onGoHome={this.handleGoHome}
        onReportBug={this.handleReportBug}
      />
    }

    return this.props.children
  }
}

// Error Fallback Component
interface ErrorFallbackProps {
  error: Error | null
  errorId: string | null
  onRetry: () => void
  onReload: () => void
  onGoHome: () => void
  onReportBug: () => void
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  errorId,
  onRetry,
  onReload,
  onGoHome,
  onReportBug
}) => {
  const [mode] = React.useState<'light' | 'dark'>(() => {
    if (typeof window === 'undefined') return 'light'
    const stored = localStorage.getItem('wg_theme_mode')
    if (stored === 'light' || stored === 'dark') return stored
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })

  const is404Error = error?.message?.includes('404') || 
                    error?.message?.includes('Not Found') ||
                    window.location.pathname.includes('404')

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: mode === 'dark'
          ? `linear-gradient(135deg, ${alpha('#0f1419', 0.95)} 0%, ${alpha('#1a252f', 0.95)} 100%)`
          : `linear-gradient(135deg, ${alpha('#f8f9fa', 0.95)} 0%, ${alpha('#ffffff', 0.95)} 100%)`,
        p: 2
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: 4,
            background: mode === 'dark'
              ? `linear-gradient(135deg, ${alpha('#1a252f', 0.8)} 0%, ${alpha('#2c3e50', 0.8)} 100%)`
              : `linear-gradient(135deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#f8f9fa', 0.9)} 100%)`,
            border: `1px solid ${alpha('#1976d2', 0.1)}`,
            boxShadow: `0 8px 32px ${alpha('#1976d2', 0.1)}`,
            textAlign: 'center'
          }}
        >
          <Stack spacing={4} alignItems="center">
            {/* Error Icon */}
            <Box
              sx={{
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha('#1976d2', 0.1),
                color: '#1976d2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ErrorOutline sx={{ fontSize: '4rem' }} />
            </Box>

            {/* Error Title */}
            <Box>
              <Typography
                variant="h2"
                fontWeight={900}
                sx={{
                  mb: 2,
                  background: `linear-gradient(135deg, ${'#1976d2'} 0%, ${'#dc004e'} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '3rem', md: '4rem' }
                }}
              >
                {is404Error ? '404' : 'Oops!'}
              </Typography>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: mode === 'dark' ? '#ffffff' : '#1976d2',
                  mb: 2
                }}
              >
                {is404Error ? 'Seite nicht gefunden' : 'Etwas ist schiefgelaufen'}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(25, 118, 210, 0.6)',
                  maxWidth: 600,
                  mx: 'auto',
                  lineHeight: 1.6
                }}
              >
                {is404Error 
                  ? 'Die gesuchte Seite existiert leider nicht oder wurde verschoben. Überprüfen Sie die URL oder kehren Sie zur Startseite zurück.'
                  : 'Ein unerwarteter Fehler ist aufgetreten. Unser Team wurde automatisch benachrichtigt und arbeitet an einer Lösung.'
                }
              </Typography>
            </Box>

            {/* Error ID */}
            {errorId && (
              <Chip
                label={`Fehler-ID: ${errorId}`}
                size="small"
                sx={{
                  bgcolor: alpha('#1976d2', 0.1),
                  color: '#1976d2',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}
              />
            )}

            {/* Action Buttons */}
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2} 
              sx={{ width: '100%', maxWidth: 500 }}
            >
              <Button
                onClick={onGoHome}
                variant="contained"
                startIcon={<Home />}
                fullWidth
                sx={{
                  py: 1.5,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${'#1976d2'} 0%, ${'#dc004e'} 100%)`,
                  boxShadow: `0 4px 16px ${alpha('#1976d2', 0.3)}`,
                  fontSize: '1rem',
                  fontWeight: 600,
                  '&:hover': {
                    boxShadow: `0 6px 20px ${alpha('#1976d2', 0.4)}`,
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                Zur Startseite
              </Button>
              
              <Button
                onClick={onRetry}
                variant="outlined"
                startIcon={<Refresh />}
                fullWidth
                sx={{
                  py: 1.5,
                  borderRadius: 3,
                  borderColor: alpha('#1976d2', 0.3),
                  color: '#1976d2',
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderWidth: 2,
                  '&:hover': {
                    borderColor: '#1976d2',
                    bgcolor: alpha('#1976d2', 0.1),
                    transform: 'translateY(-1px)',
                    borderWidth: 2
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                Erneut versuchen
              </Button>
            </Stack>

            {/* Additional Actions */}
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2}
              sx={{ width: '100%', maxWidth: 400 }}
            >
              <Button
                onClick={onReload}
                variant="text"
                startIcon={<Refresh />}
                sx={{
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(25, 118, 210, 0.6)',
                  '&:hover': {
                    color: '#1976d2',
                    bgcolor: alpha('#1976d2', 0.1)
                  }
                }}
              >
                Seite neu laden
              </Button>
              
              <Button
                onClick={onReportBug}
                variant="text"
                startIcon={<BugReport />}
                sx={{
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(25, 118, 210, 0.6)',
                  '&:hover': {
                    color: '#1976d2',
                    bgcolor: alpha('#1976d2', 0.1)
                  }
                }}
              >
                Fehler melden
              </Button>
            </Stack>

            {/* Support Information */}
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: alpha('#1976d2', 0.1),
                border: `1px solid ${alpha('#1976d2', 0.2)}`,
                width: '100%'
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
                <Support sx={{ color: '#1976d2' }} />
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: '#1976d2', mb: 0.5 }}
                  >
                    Benötigen Sie Hilfe?
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(25, 118, 210, 0.6)'
                    }}
                  >
                    Kontaktieren Sie uns unter{' '}
                    <a 
                      href="mailto:support@wg-anzeigen.de" 
                      style={{ 
                        color: '#1976d2', 
                        textDecoration: 'none',
                        fontWeight: 600
                      }}
                    >
                      support@wg-anzeigen.de
                    </a>
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Container>
    </Box>
  )
}

export default ErrorBoundary
